/**
 * 全局快捷键。输入框聚焦时全部让行（除 Esc）。
 * 键位表见 PLAN §9.3；M1 新增：Delete / Ctrl+A / Ctrl+G / Ctrl+Shift+G / Ctrl+D / 方向键 / Ctrl+[ ]
 * ⚠️ Delete / Backspace 删除元素**当前不开放**（用户 2026-10-13 要求），见下面被注释的分支。
 * ⚠️ 可添加图形的键位 L / R / E / P 同样**当前不开放**（同一天的要求），见下面被注释的 TOOL_KEYS。
 */
import { onBeforeUnmount, onMounted } from 'vue';
import type { OrderMode } from '@/core/ops/structure';
import { ui, type Tool } from '@/stores/editor';

const isTyping = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
};

export interface HotkeyDeps {
  undo(): void;
  redo(): void;
  zoomFit(): void;
  zoomToSelection(): void;
  setZoom(zoom: number): void;
  clearSelection(): void;
  deleteSelection(): boolean;
  selectAll(): void;
  group(): boolean;
  ungroup(): boolean;
  duplicate(): boolean;
  reorder(mode: OrderMode): boolean;
  nudge(dx: number, dy: number): boolean;
  exitContext(): boolean;
  copy(): number;
  cut(): boolean;
  paste(): boolean;
  editText(): boolean;
  /** 就地文本编辑是否进行中（进行中时不抢 Esc） */
  isEditingText(): boolean;
  setTool(tool: Tool): void;
  exportSvg(): void;
  exportProject(): void;
  openFromClipboard(): void;
  toggleCode(): void;
}

export function useHotkeys(deps: HotkeyDeps): void {
  const onKey = (e: KeyboardEvent): void => {
    if (isTyping(e.target) && e.key !== 'Escape') return;
    const mod = e.ctrlKey || e.metaKey;
    const key = e.key;

    if (e.key === 'Escape') {
      if (deps.isEditingText()) return; // 交给编辑框自己收尾
      ui.tool = 'select'; // 绘制工具随时可收回
      if (!deps.exitContext()) deps.clearSelection();
      return;
    }

    if (mod) {
      switch (key.toLowerCase()) {
        case 'z':
          e.preventDefault();
          (e.shiftKey ? deps.redo : deps.undo)();
          return;
        case 'y':
          e.preventDefault();
          deps.redo();
          return;
        case 'a':
          e.preventDefault();
          deps.selectAll();
          return;
        case 'g':
          e.preventDefault();
          (e.shiftKey ? deps.ungroup : deps.group)();
          return;
        case 'd':
          e.preventDefault();
          deps.duplicate();
          return;
        case 's':
          e.preventDefault();
          (e.shiftKey ? deps.exportProject : deps.exportSvg)();
          return;
        case 'e':
          e.preventDefault();
          deps.toggleCode();
          return;
        case 'c':
          e.preventDefault();
          deps.copy();
          return;
        case 'x':
          e.preventDefault();
          deps.cut();
          return;
        case 'v':
          e.preventDefault();
          // Ctrl+Shift+V = 把系统剪贴板里的 SVG 当新文档打开；Ctrl+V = 内部元素粘贴
          if (e.shiftKey) deps.openFromClipboard();
          else deps.paste();
          return;
        case ']':
          e.preventDefault();
          deps.reorder(e.shiftKey ? 'front' : 'forward');
          return;
        case '[':
          e.preventDefault();
          deps.reorder(e.shiftKey ? 'back' : 'backward');
          return;
        case '0':
          e.preventDefault();
          deps.setZoom(1);
          return;
        case '1':
          e.preventDefault();
          deps.zoomFit();
          return;
        case '2':
          e.preventDefault();
          deps.zoomToSelection();
          return;
        case '=':
        case '+':
          e.preventDefault();
          deps.setZoom(ui.zoom * 1.25);
          return;
        case '-':
          e.preventDefault();
          deps.setZoom(ui.zoom / 1.25);
          return;
        default:
          return;
      }
    }

    if (key === 'Enter' || key === 'F2') {
      if (deps.editText()) e.preventDefault();
      return;
    }

    // 键盘删除：暂时关闭，不给用户开放（用户 2026-10-13 要求「Delete 键也不能删元素」）。
    // 要放出来就把下面这段取消注释。
    // if (key === 'Delete' || key === 'Backspace') {
    //   e.preventDefault();
    //   deps.deleteSelection();
    //   return;
    // }

    if (key.startsWith('Arrow')) {
      e.preventDefault();
      const step = e.shiftKey ? 10 : 1;
      const dx = key === 'ArrowLeft' ? -step : key === 'ArrowRight' ? step : 0;
      const dy = key === 'ArrowUp' ? -step : key === 'ArrowDown' ? step : 0;
      if (dx !== 0 || dy !== 0) deps.nudge(dx, dy);
      return;
    }

    // 单键切换工具（与参考产品一致）
    // ⚠️ 可添加图形的键位（L / R / E / P）**暂时关闭**，与工具条上的绘图按钮一起收起来
    // （用户 2026-10-13 要求），否则按钮没了但按键仍能进绘图工具 = 没关干净。
    // 要放出来就把下面四行取消注释，同时把 TopBar 里的绘图按钮一起放出来。
    const TOOL_KEYS: Record<string, Tool> = {
      v: 'select',
      h: 'hand',
      t: 'text',
      // l: 'line',
      // r: 'rect',
      // e: 'ellipse',
      // p: 'polyline',
    };
    const tool = TOOL_KEYS[key.toLowerCase()];
    if (tool) ui.tool = tool;
    if (key === ' ') ui.tool = 'hand';
  };

  onMounted(() => document.addEventListener('keydown', onKey));
  onBeforeUnmount(() => document.removeEventListener('keydown', onKey));
}
